import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useConfigStore } from "@/stores/config";
import { createPermissionGuard } from "@/core/permissions/permissions";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/landing",
    name: "landing",
    component: () => import("@/views/LandingPage.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: () => import("@/views/PrivacyPolicy.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/public/bottle-detail/:asset_no",
    name: "public-bottle-detail",
    component: () => import("@/views/apps/bottle-management/WineBottleInfo.vue"),
    meta: {
      requiresAuth: false,
      pageTitle: "Bottle Information",
    },
  },
  {
    path: "/public/menu/:id",
    name: "public-menu",
    component: () => import("@/views/apps/menu-builder/MenuViewer.vue"),
    meta: {
      requiresAuth: false,
      pageTitle: "Wine Menu",
    },
  },
  {
    path: "/public/menu-detail/:wine_id",
    name: "public-menu-wine-detail",
    component: () => import("@/views/apps/menu-builder/MenuViewerDetail.vue"),
    meta: {
      requiresAuth: false,
      pageTitle: "Wine Menu",
    },
  },
  // Backward-compat: existing bottle QR codes encode /wine-bottles/info/:asset_no
  // and old menu links use /wine-menu/view/:id. Redirect both to the new /public/* paths
  // so previously printed QR codes and shared links keep working.
  {
    path: "/wine-bottles/info/:asset_no",
    redirect: (to) => ({
      name: "public-bottle-detail",
      params: { asset_no: to.params.asset_no },
    }),
  },
  {
    path: "/wine-menu/view/:id",
    redirect: (to) => ({ name: "public-menu", params: { id: to.params.id } }),
  },
  {
    path: "/",
    redirect: "/landing",
    component: () => import("@/layouts/default-layout/DefaultLayout.vue"),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/views/Dashboard.vue"),
        meta: {
          // pageTitle: "menu.dashboard",
        },
      },
      // {
      //   path: "/builder",
      //   name: "builder",
      //   component: () => import("@/views/LayoutBuilder.vue"),
      //   meta: {
      //     pageTitle: "LayoutBuilder.pageTitle",
      //     breadcrumbs: ["LayoutBuilder.breadcrumb"],
      //   },
      // },
      {
        path: "/wines",
        name: "wines-listing",
        component: () => import("@/views/apps/wine-management/WineListing.vue"),
        meta: {
          pageTitle: "WineListing.pageTitle",
          breadcrumbs: ["WineListing.breadcrumb"],
          permissions: ["wine:view"],
          beverageType: "wine",
        },
      },
      {
        path: "/wines/:id",
        name: "wines-detail",
        component: () => import("@/views/apps/wine-management/WineDetail.vue"),
        meta: {
          breadcrumbs: ["wine.list.breadcrumb", "common.details"],
          permissions: ["wine:view"],
          beverageType: "wine",
        },
      },

      {
        path: "/wine-bottles/:receipt_item_id/bottles",
        name: "wine-bottles-group",
        component: () => import("@/views/apps/bottle-management/BottleListing.vue"),
        meta: {
          pageTitle: "BottleListing.pageTitle",
          breadcrumbs: ["ReceiptItemListing.breadcrumb", "BottleListing.breadcrumb"],
          permissions: ["bottle:view"],
        },
      },
      {
        path: "/wine-bottles",
        name: "wine-bottles-listing",
        component: () => import("@/views/apps/bottle-management/BottleListing.vue"),
        meta: {
          pageTitle: "BottleListing.pageTitle",
          breadcrumbs: ["BottleListing.breadcrumb"],
          permissions: ["bottle:view"],
        },
      },
      {
        path: "/system-config",
        name: "system-config",
        component: () => import("@/views/apps/system-config/SystemConfigPage.vue"),
        meta: {
          pageTitle: "SystemConfig.pageTitle",
          breadcrumbs: ["SystemConfig.breadcrumb"],
          requiresAuth: true,
          superadmin: true,
        },
      },
      {
        path: "/categories",
        name: "categories-listing",
        component: () => import("@/views/apps/category-management/CategoryListing.vue"),
        meta: {
          pageTitle: "CategoryListing.pageTitle",
          breadcrumbs: ["CategoryListing.breadcrumb"],
          permissions: ["wine:view"],
        },
      },
      {
        path: "/wine-locations",
        name: "wine-locations-listing",
        component: () => import("@/views/apps/location-management/LocationListing.vue"),
        meta: {
          pageTitle: "LocationListing.pageTitle",
          breadcrumbs: ["LocationListing.breadcrumb"],
          permissions: ["location:view"],
        },
      },
      {
        path: "/reports/stock-on-hand",
        name: "stock-on-hand-report",
        component: () => import("@/views/apps/reports/StockOnHandReport.vue"),
        meta: {
          breadcrumbs: ["StockOnHandReport.breadcrumb"],
          permissions: ["stock_on_hand_report:view"],
        },
      },
      {
        path: "/reports/audit",
        name: "audit-report",
        component: () => import("@/views/apps/reports/AuditReport.vue"),
        meta: {
          // pageTitle: "AuditReport.pageTitle",
          breadcrumbs: ["AuditReport.breadcrumb"],
          permissions: ["audit_report:view"],
        },
      },
      {
        path: "/reports/rfid",
        name: "rfid-tracking",
        component: () => import("@/views/apps/reports/RfidTracking.vue"),
        meta: {
          // pageTitle: "RfidTracking.pageTitle",
          breadcrumbs: ["RfidTracking.breadcrumb"],
          permissions: ["rfid_report:view"],
        },
      },
      {
        path: "/reports/activity-logs",
        name: "activity-logs",
        component: () => import("@/views/apps/reports/ActivityLogsListing.vue"),
        meta: {
          // pageTitle: "ActivityLogsListing.pageTitle",
          breadcrumbs: ["ActivityLogsListing.breadcrumbReports", "ActivityLogsListing.breadcrumb"],
          permissions: ["activity_logs:view"],
        },
      },
      {
        path: "/reports/receiving",
        name: "receiving-reports",
        component: () => import("@/views/apps/reports/ReceivingReports.vue"),
        meta: {
          breadcrumbs: ["ReceivingListing.breadcrumbReports", "ReceivingListing.breadcrumb"],
          permissions: ["receiving_report:view"],
        },
      },
      {
        path: "/settings/admin/users-listing",
        name: "settings-users-lists",
        component: () => import("@/views/apps/admin-management/UserListing.vue"),
        meta: {
          pageTitle: "UserListing.pageTitle",
          breadcrumbs: ["common.settings", "common.users"],
          permissions: ["user:view"],
        },
      },
      {
        path: "/settings/admin/users/:id",
        name: "settings-users-details",
        component: () => import("@/views/apps/admin-management/UserDetail.vue"),
        meta: {
          pageTitle: "UserDetail.pageTitle",
          breadcrumbs: ["common.settings", "common.users"],
          permissions: ["user:view"],
        },
      },
      {
        path: "/settings/companies",
        name: "settings-companies-listing",
        component: () => import("@/views/apps/company-management/CompanyListing.vue"),
        meta: {
          pageTitle: "CompanyListing.pageTitle",
          breadcrumbs: ["common.settings", "common.companies"],
          permissions: ["company:view"],
        },
      },
      {
        path: "/settings/companies/:id",
        name: "settings-companies-detail",
        component: () => import("@/views/apps/company-management/CompanyDetail.vue"),
        meta: {
          pageTitle: "CompanyDetail.pageTitle",
          breadcrumbs: ["common.settings", "common.companies"],
          permissions: ["company:view"],
        },
      },
      {
        path: "/settings/roles-listing",
        name: "settings-role-listing",
        component: () => import("@/views/apps/admin-management/RoleListing.vue"),
        meta: {
          pageTitle: "RoleListing.pageTitle",
          breadcrumbs: ["common.settings", "common.roles"],
          permissions: ["role:view"],
        },
      },
      {
        path: "/settings/roles/:id",
        name: "settings-role-detail",
        component: () => import("@/views/apps/admin-management/RoleDetail.vue"),
        meta: {
          pageTitle: "RoleDetail.pageTitle",
          breadcrumbs: ["common.settings", "common.roles", "common.details"],
          permissions: ["role:view"],
        },
      },
      {
        path: "/settings/roles/new",
        name: "settings-role-create",
        component: () => import("@/views/apps/admin-management/RoleAddModal.vue"),
        meta: {
          pageTitle: "RoleAddModal.pageTitle",
          breadcrumbs: ["common.settings", "common.roles"],
          permissions: ["role:create"],
        },
      },
      {
        path: "/settings/user-groups",
        name: "settings-user-groups-listing",
        component: () => import("@/views/apps/admin-management/UserGroupListing.vue"),
        meta: {
          pageTitle: "UserGroupListing.pageTitle",
          breadcrumbs: ["common.settings", "UserGroupListing.breadcrumb"],
          permissions: ["user-group:view"],
        },
      },
      {
        path: "/settings/bottle-statuses",
        name: "settings-bottle-statuses",
        component: () => import("@/views/apps/settings/BottleStatusListing.vue"),
        meta: {
          pageTitle: "BottleStatusListing.pageTitle",
          breadcrumbs: ["common.settings", "BottleStatusListing.breadcrumb"],
          permissions: ["settings:view"],
        },
      },
      {
        path: "/notifications",
        name: "notifications",
        component: () => import("@/views/apps/notifications/NotificationListing.vue"),
        meta: {
          pageTitle: "Notification.pageTitle",
          breadcrumbs: ["Notification.breadcrumb"],
          permissions: ["notification:view"],
        },
      },
      {
        path: "/settings/notifications",
        name: "settings-notifications",
        component: () => import("@/views/apps/notifications/NotificationSettings.vue"),
        meta: {
          pageTitle: "NotificationSettings.pageTitle",
          breadcrumbs: ["common.settings", "NotificationSettings.breadcrumb"],
          permissions: ["notification:setting"],
        },
      },
      // {
      //   path: "/settings/packages",
      //   name: "settings-packages",
      //   component: () => import("@/views/apps/subscription/PackageListing.vue"),
      //   meta: {
      //     pageTitle: "PackageListing.pageTitle",
      //     breadcrumbs: ["common.settings", "PackageListing.breadcrumb"],
      //     permissions: ["package:view"], // Or superadmin only
      //   },
      // },
      // {
      //   path: "/settings/subscriptions",
      //   name: "settings-subscriptions",
      //   component: () => import("@/views/apps/subscription/SubscriptionManagement.vue"),
      //   meta: {
      //     pageTitle: "SubscriptionManagement.pageTitle",
      //     breadcrumbs: ["common.settings", "SubscriptionManagement.breadcrumb"],
      //     permissions: ["subscription:view"],
      //   },
      // },
      {
        path: "/menu-builder",
        name: "menu-builder",
        component: () => import("@/views/apps/menu-builder/MenuBuilderPage.vue"),
        meta: {
          pageTitle: "Digital Wine Menu",
          breadcrumbs: ["menu.menuBuilder"],
          permissions: ["wine:view"],
        },
      },
      {
        path: "/menu-builder/:id/edit",
        name: "menu-builder-editor",
        component: () => import("@/views/apps/menu-builder/MenuBuilderEditor.vue"),
        meta: {
          pageTitle: "Edit Wine Menu",
          breadcrumbs: ["menu.menuBuilder", "common.edit"],
          permissions: ["wine:view"],
        },
      },
      {
        path: "/v1/user-manual",
        name: "cellar-management-manual",
        component: () => import("@/views/apps/docs/CellarManagementManual.vue"),
        meta: {
          pageTitle: "CellarManagementManual.pageTitle",
          breadcrumbs: ["menu.wineManagementSection", "CellarManagementManual.breadcrumb"],
          permissions: ["wine:view"],
        },
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/layouts/AuthLayout.vue"),
    children: [
      {
        path: "/oauth/callback",
        name: "oauth-callback",
        component: () => import("@/views/authentication/OAuthCallback.vue"),
        meta: { pageTitle: "OAuthCallback.pageTitle" },
      },
      {
        path: "/logon",
        name: "logon",
        component: () => import("@/views/authentication/Logon.vue"),
        meta: { pageTitle: "Logon.pageTitle" },
      },
      {
        path: "/accept-invite",
        name: "accept-invite",
        component: () => import("@/views/authentication/AcceptInvite.vue"),
        meta: { pageTitle: "AcceptInvite.pageTitle" },
      },
      {
        path: "/debug-login",
        name: "debug-login",
        component: () => import("@/views/authentication/DebugLogin.vue"),
        meta: { pageTitle: "DebugLogin.pageTitle" },
      },
      {
        path: "/sign-in",
        name: "sign-in",
        component: () =>
          import("@/views/authentication/SignIn.vue"),
        meta: {
          pageTitle: "SignIn.pageTitle",
        },
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore();

          // Check if user already has a valid token
          if (authStore.isAuthenticated && authStore.user?._id) {
            // User is already authenticated, redirect to intended page or dashboard
            const redirectPath = sessionStorage.getItem('redirect_after_login') || '/dashboard';
            sessionStorage.removeItem('redirect_after_login');
            next(redirectPath);
            return;
          }

          // Try to verify authentication with the API
          try {
            await authStore.verifyAuth();

            // If verification successful, redirect to intended page or dashboard
            if (authStore.isAuthenticated && authStore.user?._id) {
              const redirectPath = sessionStorage.getItem('redirect_after_login') || '/dashboard';
              sessionStorage.removeItem('redirect_after_login');
              next(redirectPath);
              return;
            }
          } catch (error) {
            // If verification fails, continue to sign-in page
            console.log('Not authenticated, showing sign-in page');
          }

          // Not authenticated, proceed to sign-in page
          next();
        },
      },
      {
        path: "/sign-in-password",
        name: "sign-in-password",
        component: () =>
          import("@/views/authentication/SignIn.vue"),
        meta: {
          pageTitle: "SignIn.pageTitle",
        },
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore();

          // Check if user already has a valid token
          if (authStore.isAuthenticated && authStore.user?._id) {
            // User is already authenticated, redirect to intended page or dashboard
            const redirectPath = sessionStorage.getItem('redirect_after_login') || '/dashboard';
            sessionStorage.removeItem('redirect_after_login');
            next(redirectPath);
            return;
          }

          // Try to verify authentication with the API
          try {
            await authStore.verifyAuth();

            // If verification successful, redirect to intended page or dashboard
            if (authStore.isAuthenticated && authStore.user?._id) {
              const redirectPath = sessionStorage.getItem('redirect_after_login') || '/dashboard';
              sessionStorage.removeItem('redirect_after_login');
              next(redirectPath);
              return;
            }
          } catch (error) {
            // If verification fails, continue to sign-in page
            console.log('Not authenticated, showing sign-in page');
          }

          // Not authenticated, proceed to sign-in page
          next();
        },
      },
      // {
      //   path: "/sign-up",
      //   name: "sign-up",
      //   component: () =>
      //     import("@/views/authentication/SignUp.vue"),
      //   meta: {
      //     pageTitle: "Sign Up",
      //   },
      // },
      // {
      //   path: "/password-reset",
      //   name: "password-reset",
      //   component: () =>
      //     import("@/views/authentication/PasswordReset.vue"),
      //   meta: {
      //     pageTitle: "Password reset",
      //   },
      // },
    ],
  },
  {
    path: "/",
    component: () => import("@/layouts/SystemLayout.vue"),
    children: [{
      path: "/404",
      name: "404",
      component: () => import("@/views/errors/Error404.vue"),
      meta: {
        pageTitle: "Error404.pageTitle",
      },
    },
    {
      path: "/403",
      name: "403",
      component: () => import("@/views/errors/Error403.vue"),
      meta: {
        pageTitle: "Error403.pageTitle",
      },
    },
    {
      path: "/500",
      name: "500",
      component: () => import("@/views/errors/Error500.vue"),
      meta: {
        pageTitle: "Error500.pageTitle",
      },
    },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    // If the route has a hash, scroll to the section with the specified ID; otherwise, scroll toc the top of the page.
    if (to.hash) {
      return {
        el: to.hash,
        top: 80,
        behavior: "smooth",
      };
    } else {
      return {
        top: 0,
        left: 0,
        behavior: "smooth",
      };
    }
  },
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const configStore = useConfigStore();

  // current page view title
  // document.title = `${to.meta.pageTitle} - ${import.meta.env.VITE_APP_NAME}`;
  document.title = `${import.meta.env.VITE_APP_NAME}`;

  // reset config to initial state
  configStore.resetLayoutConfig();

  // Create and use the permission guard (now handles auth verification internally)
  const permissionGuard = createPermissionGuard();
  await permissionGuard(to, from, next);
});

export default router;
